import React, { useEffect, useRef, useState } from "react";
import { Box, HStack, Text, Button, Badge, IconButton } from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import { isOverdue, isLongDescription, withStatus } from "./tasksModel";
import { normalizePriority } from "../../utils/taskPriority";
import WorkItemActions from "./WorkItemActions";
import PersonChip from "./PersonChip";
import WorkItemActivity from "./WorkItemActivity";
import EditTaskModal from "./EditTaskModal";
import PriorityIcon from "./PriorityIcon";
import PaperworkAttachment from "./PaperworkAttachment";

// Tasks made from a SuperAdmin → Live Activity dashboard row, or from a chart's
// signed order (Orders → Paperwork → Assign as task).
export const SOURCE_BADGE = {
  memory_issue: { label: "Memory issue", colorScheme: "orange" },
  db_health: { label: "DB issue", colorScheme: "red" },
  code_review: { label: "Code issue", colorScheme: "purple" },
  order_paperwork: { label: "Order paperwork", colorScheme: "blue" },
};

export const KIND_BADGE = {
  task: { label: "Task", colorScheme: "purple" },
  order: { label: "Order", colorScheme: "blue" },
  followup: { label: "Follow-up", colorScheme: "teal" },
};

export function formatDate(value) {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// One task or order in the Tasks list — also what a board card opens.
export default function TaskRow({ item, view, currentUserId, currentUserSchema, summary, onItemChanged, onCopy, highlighted }) {
  const [showActivity, setShowActivity] = useState(false);
  const [commentFocus, setCommentFocus] = useState(0);
  // Comments the open activity has shown: newer than the summary when someone
  // else just commented. Counts only grow, so the larger number is the current one.
  const [seenComments, setSeenComments] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [editing, setEditing] = useState(false);
  const longDescription = isLongDescription(item.description);
  const badge = KIND_BADGE[item.kind === "order" ? item.orderType : "task"] || KIND_BADGE.order;
  const overdue = !item.done && isOverdue(item.due);
  // The person that matters on this side, shown big: who has it (Assigned by
  // me, or someone else's item in a channel) or who handed it to me (Assigned to me).
  const lookingOn = view === "others";
  const who =
    view === "assigned" || lookingOn
      ? { label: "Assigned to", name: item.assigneeName || "someone", email: item.assigneeEmail, schema: item.assigneeSchema }
      : item.assignerName
        ? { label: "From", name: item.assignerName, schema: item.assignerSchema }
        : null;
  const rejection = item.rejected
    ? view === "mine"
      ? "You rejected this"
      : `Rejected by ${item.assigneeName || "them"}${summary?.rejection?.body ? `: “${summary.rejection.body}”` : ""}`
    : null;
  const comments = Math.max(summary?.comments || 0, seenComments);
  const reviewNote =
    item.status === "pending_review"
      ? view === "assigned"
        ? `Marked done by ${item.assigneeName || "the assignee"} — needs your review`
        : lookingOn
          ? `Marked done by ${item.assigneeName || "the assignee"} — waiting for ${item.assignerName || "review"}`
          : `Waiting for ${item.assignerName || "review"}`
      : null;
  const rowRef = useRef(null);
  useEffect(() => {
    if (highlighted) rowRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [highlighted]);

  return (
    <HStack
      ref={rowRef}
      align="flex-start"
      spacing={3}
      // In a narrow window (a board card's) the actions wrap below the text
      // instead of squeezing it into a strip.
      flexWrap="wrap"
      p={3}
      borderWidth="1px"
      borderRadius="md"
      bg={item.done || item.cancelled ? "gray.50" : "white"}
      data-testid={`task-row-${item.key}`}
      data-highlighted={highlighted ? "true" : undefined}
      borderColor={highlighted ? "blue.400" : undefined}
      boxShadow={highlighted ? "0 0 0 3px rgba(66, 153, 225, 0.6)" : undefined}
    >
      <Box flex="1 1 320px" minW={0} data-testid="task-row-body">
        <HStack spacing={2} mb={1} flexWrap="wrap">
          <PriorityIcon priority={item.priority} />
          <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
          {SOURCE_BADGE[item.sourceKind] && (
            <Badge colorScheme={SOURCE_BADGE[item.sourceKind].colorScheme} variant="subtle">
              {SOURCE_BADGE[item.sourceKind].label}
            </Badge>
          )}
          {item.patientName && (
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              {item.patientName}
            </Text>
          )}
        </HStack>
        <Text fontWeight="medium" textDecoration={item.done || item.cancelled ? "line-through" : "none"}
              color={item.done || item.cancelled ? "gray.500" : "inherit"}>
          {item.title}
        </Text>
        {who && (
          <HStack mt={1} spacing={2} flexWrap="wrap">
            <PersonChip label={who.label} name={who.name} email={who.email} schema={who.schema} />
            {lookingOn && item.assignerName && (
              <PersonChip label="From" name={item.assignerName} schema={item.assignerSchema} />
            )}
          </HStack>
        )}
        {item.description && (
          <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap"
                noOfLines={longDescription && !showFullDescription ? 3 : undefined}>
            {item.description}
          </Text>
        )}
        {longDescription && (
          <Button size="xs" variant="link" colorScheme="blue" aria-expanded={showFullDescription}
                  onClick={() => setShowFullDescription((v) => !v)}>
            {showFullDescription ? "Show less" : "Show more"}
          </Button>
        )}
        {item.sourceKind === "order_paperwork" && item.sourceRef && (
          <PaperworkAttachment sourceRef={item.sourceRef} schema={item.sourceSchema} />
        )}
        {item.sourceLabel && (
          <Text fontSize="xs" color="gray.500">
            {item.sourceLabel}
          </Text>
        )}
        {reviewNote && (
          <Text fontSize="xs" color="orange.600" fontWeight="medium">
            {reviewNote}
          </Text>
        )}
        {rejection && (
          <Text fontSize="sm" color="red.600" fontWeight="medium" whiteSpace="pre-wrap">
            {rejection}
          </Text>
        )}
        <HStack spacing={3} mt={1} fontSize="xs" color="gray.500" flexWrap="wrap">
          {item.due && (
            <Text color={overdue ? "red.500" : "gray.500"} fontWeight={overdue ? "semibold" : "normal"}>
              {overdue ? "Overdue · " : "Due "}
              {formatDate(item.due)}
            </Text>
          )}
          {item.visitDate && <Text>Visit {formatDate(item.visitDate)}</Text>}
          <Button size="xs" variant="link" colorScheme="blue" aria-expanded={showActivity}
                  onClick={() => setShowActivity((v) => !v)}>
            {comments ? `Activity · 💬 ${comments}` : "Activity"}
          </Button>
        </HStack>
        {showActivity && (
          <WorkItemActivity
            item={{ kind: item.kind, id: item.id, schema: item.schema, assignedTo: item.assignedTo, assignedBy: item.assignedBy }}
            currentUserId={currentUserId}
            focusComment={commentFocus}
            onCommentCount={setSeenComments}
          />
        )}
      </Box>
      <HStack spacing={1} align="flex-start">
        <IconButton
          aria-label={`Copy task: ${item.title}`}
          icon={<CopyIcon />}
          size="xs"
          variant="ghost"
          onClick={() => onCopy(item)}
        />
        <WorkItemActions
          item={item}
          currentUserId={currentUserId}
          currentUserSchema={currentUserSchema}
          onComment={() => {
            setShowActivity(true);
            setCommentFocus((n) => n + 1);
          }}
          onEdit={() => setEditing(true)}
          onChanged={(s) => onItemChanged(withStatus(item, s))}
        />
      </HStack>
      {editing && (
        <EditTaskModal
          task={item}
          currentUserId={currentUserId}
          onClose={() => setEditing(false)}
          onSaved={(saved) =>
            saved && onItemChanged({
              ...item,
              title: saved.title,
              description: saved.description || null,
              due: saved.due_date || null,
              priority: normalizePriority(saved.priority ?? item.priority),
            })
          }
        />
      )}
    </HStack>
  );
}
